import * as React from 'react'
import { async, fromJSX, unloadedAsyncState, loadingAsyncState } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import type { DP } from '../../../drupal/blockDefinitions'
import { dummyData, randomTitle, randomWords } from '../../../utils/dummyData'
import { linkWrapper } from '../../../utils/link'
import * as WP from '../../../wordpress/blockDefinitions'
import type { ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { recentNewsBlockClasses, RecentNewsBlockLayout} from './recentNewsBlock.layout'
import { loadRecentNews } from './recentNewsBlock.api'
import type { RecentNewsBlockProps } from './recentNewsBlock.state'
import { Entity } from 'ts-lenses'
import { ParsedOverviewDetailItem } from '../../../customPages/overviewPage/widgets/overviewWidget.state'

export const recentNewsBlock: ModernBlock<RecentNewsBlockProps, WP.Gutenberg.Vidda.Text, any, DP.RecentNews> = {
  kind: 'recentNewsBlock',
  render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
    fromJSX((updateBlock) => (
      <RecentNewsBlockLayout.Inner
        id={`formstack-${blockIndex}`}
      >
        <RecentNewsBlockLayout.Content
          label={blockProps.label}
          title={blockProps.title}
          intro={blockProps.intro}
          showPrimary={blockProps.showPrimary}
          showSecondary={blockProps.showSecondary}
          primaryAction={blockProps.primaryAction!}
          secondaryAction={blockProps.secondaryAction!}
          showActionAsLink={blockProps.showActionAsLink}
        >
          {async<Immutable.List<ParsedOverviewDetailItem>>(blockProps.recentNews).run(a => updateBlock(s => Entity(s).set("recentNews", os => a(os)).commit()))}
          {blockProps.sgRecentNews && blockProps.sgRecentNews.length > 0 ? // USED ONLY IN STYLEGUIDE!
            <RecentNewsBlockLayout.Teasers>
              {blockProps.sgRecentNews.map((item, key) => {
                return <RecentNewsBlockLayout.Teaser
                  key={key}
                  teaserKey={`recentNewsBlock-t-${key}`}
                  link={item.link}
                  title={item.title}
                  intro={item.intro}
                  date={item.date}
                  image={item.image}
                />
              })}
            </RecentNewsBlockLayout.Teasers>
          : blockProps.recentNews.kind == "loaded" ? (
            <RecentNewsBlockLayout.Teasers>
              {blockProps.recentNews.value.map((item, key) => {
                return <RecentNewsBlockLayout.Teaser
                  key={key}
                  teaserKey={`recentNewsBlock-t-${key}`}
                  link={item.Link!}
                  title={item.Title}
                  intro={item.Description}
                  date={new Date(item.LastUpdated)}
                  image={item.image}
                />
              })}
            </RecentNewsBlockLayout.Teasers>
          ) : (
            <RecentNewsBlockLayout.Empty />
          )}
        </RecentNewsBlockLayout.Content>
      </RecentNewsBlockLayout.Inner>
    )),
  adapters: {
    drupal: (dpBlock: DP.RecentNews, blockIndex: number) => {
      const target = dpBlock.target.type[0].target_id
      if (target != 'recent_news_block') return undefined
      return {
        title: dpBlock.target.field_title ? dpBlock.target.field_title[0]?.value : undefined,
        excludeFromPageMenu: dpBlock.target?.field_exclude_from_page_menu ? dpBlock.target?.field_exclude_from_page_menu?.[0]?.value : undefined,
        label: dpBlock.target.field_label ? dpBlock.target.field_label[0]?.value : undefined,
        intro: dpBlock.target.field_introduction ? dpBlock.target.field_introduction[0]?.value : undefined,
        recentNews: loadingAsyncState(() => loadRecentNews()),
        showPrimary: dpBlock.target.field_links[0] ? true : false,
        showSecondary: dpBlock.target.field_links[1] ? true : false,
        primaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.options?.attributes?.id! : '',
        }),
        secondaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.options?.attributes?.id! : '',
        }),
        anchorTitle: dpBlock.target.field_title ? dpBlock.target.field_title[0]?.value : '',
        showActionAsLink: dpBlock.target.field_link_type[0]?.value == true ? true : false,
        inviewState: false
      }
    }
  },
  sampleExample: [
    {
      title: randomTitle(),
      label: randomWords(),
      intro: dummyData.intro,
      recentNews: unloadedAsyncState(),
      sgRecentNews: [
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        },
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        },
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        }
      ],
      anchorTitle: 'text-sample-1',
      showPrimary: true,
      showSecondary: false,
      primaryAction: dummyData.primaryButton,
      showActionAsLink: false
    },
    {
      recentNews: unloadedAsyncState(),
      sgRecentNews: [
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        },
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        },
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        },
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        },
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        },
        {
          title: randomTitle(),
          date: new Date(),
          intro: dummyData.intro,
          image: dummyData.image,
          link: {
            kind: 'link',
            url: '/',
            title: 'Lees meer',
            target: '_self',
          }
        }
      ],
      anchorTitle: 'news-overview-sample-1',
      showPrimary: false,
      showSecondary: false,
      showActionAsLink: false
    }
  ],
  seoEvents: (props, blockIndex) =>
    [
      props.showPrimary && props.primaryAction
        ? {
            id: `primary_button`,
            event: 'onClick',
            category: 'text_block',
            label: window.location.pathname,
            action: 'primary_' + props.primaryAction.title
          }
        : undefined,
      props.showSecondary && props.secondaryAction
        ? {
            id: `secondary_button`,
            event: 'onClick',
            category: 'text_block',
            label: window.location.pathname,
            action: 'secondary_' + props.secondaryAction.title
          }
        : undefined
    ].filter((i) => i != undefined) as SeoEvent[],
  inView: {
    showAs: 'section',
    threshold: 0.1,
    triggerOnce: true,
    inViewClassName: getClassNameModern(recentNewsBlockClasses, 'inview', 'inview'),
    updateState: (currentInviewState, currentBlockState) => {
      return {
        ...currentBlockState,
        inviewState: currentInviewState
      }
    }
  },
  blockClassName: getClassNameModern(recentNewsBlockClasses, 'block', 'mb-6')
}
